import React, { useId } from 'react';
import { Crosshair, Minus, Mountain, Plus, Satellite, Search } from 'lucide-react';
import { STOP_COLOR, type PlaylistStop } from '../../data/playlist-mock';

export type MapStyleName = 'map' | 'satellite' | 'terrain';

const MIN_LAT = 28.4;
const MAX_LAT = 28.82;
const MIN_LNG = 76.9;
const MAX_LNG = 77.62;
const VIEW_W = 800;
const VIEW_H = 460;
const PAD = 36;

const ROADS = [
  'M 30 150 C 160 130, 280 190, 430 170 S 680 140, 790 180',
  'M 110 40 C 180 130, 250 220, 310 340 S 430 430, 540 450',
  'M 70 300 C 200 270, 360 250, 500 275 S 700 310, 790 250',
  'M 180 30 C 260 110, 400 130, 560 70 S 730 100, 800 60',
  'M 40 220 H 760',
];

const LABELS: Array<{ x: number; y: number; text: string; strong?: boolean }> = [
  { x: 250, y: 78, text: 'Rohini' },
  { x: 360, y: 58, text: 'Pitampura' },
  { x: 150, y: 248, text: 'Janakpuri' },
  { x: 300, y: 188, text: 'Karol Bagh' },
  { x: 430, y: 214, text: 'New Delhi', strong: true },
  { x: 118, y: 360, text: 'IGI Airport' },
  { x: 560, y: 176, text: 'Mayur Vihar' },
  { x: 470, y: 268, text: 'Lajpat Nagar' },
  { x: 640, y: 300, text: 'Noida' },
  { x: 690, y: 92, text: 'Ghaziabad' },
  { x: 575, y: 150, text: 'Yamuna' },
];

export function projectStop(latitude: number, longitude: number): { x: number; y: number } {
  const x = PAD + ((longitude - MIN_LNG) / (MAX_LNG - MIN_LNG)) * (VIEW_W - PAD * 2);
  const y = PAD + ((MAX_LAT - latitude) / (MAX_LAT - MIN_LAT)) * (VIEW_H - PAD * 2);
  return { x, y };
}

interface PlaylistMapProps {
  stops: PlaylistStop[];
  selectedStopId: string | null;
  styleName: MapStyleName;
  query: string;
  zoom: number;
  onSelectStop: (id: string) => void;
  onStyle: (styleName: MapStyleName) => void;
  onQuery: (query: string) => void;
  onZoom: (zoom: number) => void;
}

function viewTransform(pins: Array<{ x: number; y: number }>, zoom: number): {
  transform: string;
  scale: number;
  cx: number;
  cy: number;
} {
  if (pins.length === 0) {
    return {
      transform: `translate(${VIEW_W / 2} ${VIEW_H / 2}) scale(${zoom}) translate(${-VIEW_W / 2} ${-VIEW_H / 2})`,
      scale: zoom,
      cx: VIEW_W / 2,
      cy: VIEW_H / 2,
    };
  }
  const xs = pins.map((pin) => pin.x);
  const ys = pins.map((pin) => pin.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const spanX = Math.max(maxX - minX, 120);
  const spanY = Math.max(maxY - minY, 90);
  const fit = Math.min(2, Math.max(1, Math.min(480 / spanX, 250 / spanY)));
  const scale = Math.min(2.6, Math.max(0.7, fit * zoom));
  return {
    transform: `translate(${VIEW_W / 2} ${VIEW_H / 2}) scale(${scale}) translate(${-cx} ${-cy})`,
    scale,
    cx,
    cy,
  };
}

function placePopup(
  anchor: { x: number; y: number },
  pins: Array<{ x: number; y: number }>,
  scale: number,
): { x: number; y: number } {
  const width = 176;
  const height = 62;
  const head = 26 * scale;
  const slots = [
    { x: anchor.x + 18, y: anchor.y - head - height - 10 },
    { x: anchor.x - width - 18, y: anchor.y - head - height - 10 },
    { x: 16, y: 96 },
    { x: 16, y: VIEW_H - height - 16 },
    { x: VIEW_W - width - 70, y: VIEW_H - height - 16 },
  ];
  let best = slots[2];
  let bestScore = Infinity;
  slots.forEach((slot) => {
    let score = 0;
    if (slot.x < 8 || slot.y < 80 || slot.x + width > VIEW_W - 56 || slot.y + height > VIEW_H - 8) {
      score += 40;
    }
    pins.forEach((pin) => {
      const pad = 16;
      const pinTop = pin.y - head;
      const hitsPin = pin.x > slot.x - pad
        && pin.x < slot.x + width + pad
        && pinTop < slot.y + height + pad
        && pin.y > slot.y - pad;
      if (hitsPin) score += 12;
    });
    const dx = slot.x + width / 2 - anchor.x;
    const dy = slot.y + height / 2 - anchor.y;
    score += Math.hypot(dx, dy) * 0.015;
    if (score < bestScore) {
      bestScore = score;
      best = slot;
    }
  });
  return best;
}

const PlaylistMap: React.FC<PlaylistMapProps> = ({
  stops,
  selectedStopId,
  styleName,
  query,
  zoom,
  onSelectStop,
  onStyle,
  onQuery,
  onZoom,
}) => {
  const uid = useId().replace(/:/g, '');
  const needle = query.trim().toLowerCase();
  const visible = stops.filter((stop) => {
    if (!needle) return true;
    return `${stop.name} ${stop.area}`.toLowerCase().includes(needle);
  });
  const placed = visible.map((stop) => ({ stop, ...projectStop(stop.latitude, stop.longitude) }));
  const selected = placed.find((pin) => pin.stop.id === selectedStopId) ?? null;
  const route = placed.map((pin, index) => `${index === 0 ? 'M' : 'L'} ${pin.x.toFixed(1)} ${pin.y.toFixed(1)}`).join(' ');
  const frame = viewTransform(placed, zoom);
  const screenPins = placed.map((pin) => ({
    id: pin.stop.id,
    x: VIEW_W / 2 + (pin.x - frame.cx) * frame.scale,
    y: VIEW_H / 2 + (pin.y - frame.cy) * frame.scale,
  }));
  const selectedScreen = screenPins.find((pin) => pin.id === selectedStopId) ?? null;
  const card = selectedScreen
    ? placePopup(selectedScreen, screenPins, frame.scale)
    : null;
  const ordered = [...placed].sort((a, b) => {
    if (a.stop.id === selectedStopId) return 1;
    if (b.stop.id === selectedStopId) return -1;
    return 0;
  });

  return (
    <div className={`pl-map pl-map--${styleName}`} data-testid="playlist-map">
      <p className="visually-hidden">
        {stops.length === 0
          ? 'Illustrated map. Add a stop to drop a pin.'
          : `Illustrated map with ${stops.length} stops.`}
      </p>
      <svg className="pl-map__svg" viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} aria-hidden="true">
        <defs>
          <filter id={`${uid}-shadow`} x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="1" stdDeviation="1.4" floodOpacity="0.28" />
          </filter>
        </defs>
        <g transform={frame.transform}>
          <rect className="pl-map__sky" width={VIEW_W} height={VIEW_H} />
          <path
            className="pl-map__land"
            d="M20 90C70 30 180 18 300 48c70-28 150-10 230-24 90-16 190 10 250 70 40 40 40 110 18 170-28 80-40 120-110 150-90 38-160 62-280 40C250 430 120 450 60 390 10 340-10 180 20 90z"
          />
          <path
            className="pl-map__land pl-map__land--deep"
            d="M80 150c60-20 120 10 180-8 50-14 90 16 150 4 40-8 70 20 120 8v40c-40 30-90 10-140 22-60 14-100-10-160 6-50 12-90-8-150-4z"
          />
          <path
            className="pl-map__water"
            d="M520 8c-30 70 40 100 8 170s40 90-10 160 30 90 8 122"
          />
          <g className="pl-map__roads">
            {ROADS.map((d) => (
              <path key={d} d={d} />
            ))}
          </g>
          <ellipse className="pl-map__park" cx="250" cy="250" rx="34" ry="22" />
          <ellipse className="pl-map__park" cx="390" cy="300" rx="28" ry="18" />
          <ellipse className="pl-map__park" cx="610" cy="250" rx="26" ry="16" />
          <g className="pl-map__airport" transform="translate(132 332)">
            <rect width="58" height="16" rx="3" />
            <path d="M-16 30H86" />
          </g>
          <g className="pl-map__labels">
            {LABELS.map((label) => (
              <text
                key={label.text}
                x={label.x}
                y={label.y}
                className={label.strong ? 'is-strong' : undefined}
              >
                {label.text}
              </text>
            ))}
          </g>
          {route && <path className="pl-map__route" d={route} />}
          {ordered.map(({ stop, x, y }) => {
            const number = stops.findIndex((item) => item.id === stop.id) + 1;
            const active = stop.id === selectedStopId;
            return (
              <g
                key={stop.id}
                className={`pl-pin${active ? ' is-active' : ''}`}
                transform={`translate(${x} ${y})`}
                filter={`url(#${uid}-shadow)`}
                onClick={() => onSelectStop(stop.id)}
              >
                <circle className="pl-pin__hit" r="18" />
                <path
                  className="pl-pin__drop"
                  d="M0-22c-8 0-14 6-14 14 0 10 14 24 14 24s14-14 14-24c0-8-6-14-14-14z"
                  style={{ fill: STOP_COLOR[stop.color] }}
                />
                <circle className="pl-pin__core" cy={-8} r={active ? 8 : 7} />
                <text className="pl-pin__num" y={-4} textAnchor="middle">
                  {number}
                </text>
                <title>{`${number}. ${stop.name}`}</title>
              </g>
            );
          })}
        </g>
        {selected && selectedScreen && card && (
          <g className="pl-popup" transform={`translate(${card.x} ${card.y})`} pointerEvents="none">
            <rect className="pl-pin__card" width="176" height="62" rx="12" />
            <text className="pl-pin__title" x="12" y="22">
              {selected.stop.name.length > 22 ? `${selected.stop.name.slice(0, 21)}…` : selected.stop.name}
            </text>
            <text className="pl-pin__sub" x="12" y="38">
              {`Stop ${stops.findIndex((item) => item.id === selected.stop.id) + 1} · ${selected.stop.area.split(',')[0]}`}
            </text>
            <text className="pl-pin__sub" x="12" y="52">
              {`${selected.stop.latitude.toFixed(4)}, ${selected.stop.longitude.toFixed(4)}`}
            </text>
          </g>
        )}
      </svg>

      <div className="pl-map__styles" role="group" aria-label="Map style">
        {([
          ['map', 'Map', null],
          ['satellite', 'Satellite', Satellite],
          ['terrain', 'Terrain', Mountain],
        ] as const).map(([id, label, Icon]) => (
          <button
            key={id}
            type="button"
            aria-pressed={styleName === id}
            onClick={() => onStyle(id)}
          >
            {Icon && <Icon size={13} aria-hidden="true" />}
            {label}
          </button>
        ))}
      </div>

      <form
        className="pl-map__search"
        role="search"
        onSubmit={(event) => event.preventDefault()}
      >
        <Search size={14} aria-hidden="true" />
        <label className="visually-hidden" htmlFor={`${uid}-map-search`}>
          Search locations on map
        </label>
        <input
          id={`${uid}-map-search`}
          type="search"
          value={query}
          placeholder="Search stops"
          onChange={(event) => onQuery(event.target.value)}
        />
      </form>

      <div className="pl-map__zoom">
        <button type="button" aria-label="Zoom in" onClick={() => onZoom(Math.min(1.8, Number((zoom + 0.2).toFixed(2))))} disabled={zoom >= 1.8}>
          <Plus size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Zoom out" onClick={() => onZoom(Math.max(0.6, Number((zoom - 0.2).toFixed(2))))} disabled={zoom <= 0.6}>
          <Minus size={16} aria-hidden="true" />
        </button>
        <button type="button" aria-label="Reset map view" onClick={() => onZoom(1)} disabled={zoom === 1}>
          <Crosshair size={16} aria-hidden="true" />
        </button>
      </div>

      {stops.length === 0 && (
        <p className="pl-map__hint">Illustrated map. Add a stop to drop a pin.</p>
      )}
    </div>
  );
};

export default PlaylistMap;
